import { createServerFn } from '@tanstack/react-start';
import {
  authenticatedServerGet,
  authenticatedServerGetEnvelope,
  assertRelativeApiPath,
} from './ssr-fetch';

/**
 * Generic cookie-forwarded GET server fn (input = API path). For auth-required
 * endpoints whose data a route loader wants to prefetch. Returns the unwrapped
 * v2 envelope payload; the loader casts to the concrete type (and wraps in a
 * Result where the component's useQuery expects one).
 *
 * The path is concatenated onto API_CONFIG.BASE_URL server-side, so only
 * root-relative paths (`^/[^/]`) are accepted — `@host`, `//origin` or an
 * absolute URL would turn the concatenation into an SSRF primitive. The
 * framework's CSRF gate trusts the client-settable Sec-Fetch-Site header, so
 * this validator is the real input gate, not a formality.
 */
export const authenticatedGetServer = createServerFn({ method: 'GET' })
  .validator((data: unknown) => {
    assertRelativeApiPath(data);
    return data as string;
  })
  .handler(async ({ data: path }) => {
    'use server';
    // Cast bypasses TanStack Start's serializability check (the runtime payload
    // is plain JSON from the API). Loaders cast to the concrete type.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime JSON; cast bypasses TanStack Start's server-fn serializability check
    return (await authenticatedServerGet(path)) as any;
  });

/**
 * Full-envelope counterpart of authenticatedGetServer (the loader-side twin of
 * apiClient.getRaw): keeps envelope siblings such as `pagination` that the
 * default unwrap drops (WriteSuccessWithPagination endpoints).
 */
export const authenticatedGetEnvelopeServer = createServerFn({ method: 'GET' })
  .validator((data: unknown) => {
    assertRelativeApiPath(data);
    return data as string;
  })
  .handler(async ({ data: path }) => {
    'use server';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- runtime JSON; cast bypasses TanStack Start's server-fn serializability check
    return (await authenticatedServerGetEnvelope(path)) as any;
  });
